import { useRef, useState } from 'react';

// material-ui
import {
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project import
import NotificationsList from './NotificationsList';

// store
import useNotificationsStore from './notificationsStore';

// api
import { useApiGeneric } from 'pages/content/api/api-generic';

// icons
import { MdDeleteSweep, MdClose, MdOutlineNotifications } from 'react-icons/md';

// Notifications popup built with the ModalDialogV2 template layout: grey
// surface, header (icon + title) and a scrollable content area. Every
// notification renders as its own capsule (see NotificationsList), there is no
// wrapping capsule around the list.
const NotificationsPopup = ({ onClose }) => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const { fetchAPI } = useApiGeneric();

  const notifications = useNotificationsStore(state => state.notifications);
  const dismissAll = useNotificationsStore(state => state.dismissAll);

  const handleDismissAll = () => {
    dismissAll(fetchAPI);
  };

  // Subtle shadow on the header once the content scrolls away from the top.
  const handleContentScroll = () => {
    const el = contentRef.current;
    if (el) setIsScrolled(el.scrollTop > 0);
  };

  // Size the icon so its glyph fills the title line-box (ModalDialogV2 pattern)
  const titleIconSize = `calc(${theme.typography.h3.fontSize} * ${theme.typography.h3.lineHeight})`;

  return (
    <Box
      sx={{
        bgcolor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxHeight: '70vh'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1,
          transition: 'box-shadow 0.2s ease',
          boxShadow: isScrolled ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="span"
            sx={{
              color: 'primary.light',
              fontSize: titleIconSize,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <MdOutlineNotifications />
          </Box>
          <Typography variant="h3" color="primary" fontWeight={300}>
            Notificaciones
          </Typography>
        </Box>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Cerrar"
            sx={{
              color: 'primary.contrastText',
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <MdClose style={{ fontSize: '2rem' }} />
          </IconButton>
        )}
      </Box>

      {/* Content (only this area scrolls) */}
      <Box
        ref={contentRef}
        onScroll={handleContentScroll}
        px={1}
        pb={1}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 0,
          '&::-webkit-scrollbar': {
            width: 14
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'grey.200',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.400',
            borderRadius: 4,
            border: 1,
            borderColor: 'grey.200',
            '&:hover': { bgcolor: 'grey.600' }
          }
        }}
      >
        <NotificationsList />
      </Box>

      {/* Actions */}
      {notifications.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            p: 2
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            startIcon={<MdDeleteSweep />}
            onClick={handleDismissAll}
          >
            Eliminar todas
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NotificationsPopup;
